
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../AuthContext';
import { useAdmin } from '../AdminContext';
import { getAIChatbotResponse, generateSpeechFromText, processSupportChatForTriage } from '../gemini';
import { decode, decodeAudioData } from '../audioUtils';
import BackButton from '../components/BackButton';
import AIConsentModal from '../components/AIConsentModal';
// FIX: Changed import to correct PageContext file to avoid circular dependency
import { PageContext } from '../PageContext';

type Message = {
    role: 'user' | 'model';
    text: string;
};

const AISupportChatPage: React.FC = () => {
    const { userProfile } = useAuth();
    const { submitAIReport } = useAdmin();
    const pageContext = useContext(PageContext);

    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [status, setStatus] = useState<'chatting' | 'processing' | 'complete'>('chatting');
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [hasGreeted, setHasGreeted] = useState(false);
    const audioContextRef = useRef<AudioContext | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Initialize AudioContext on first interaction
    const initAudioContext = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        }
    };
    
    // Initial greeting triggered ONLY after consent is confirmed
    useEffect(() => {
        if (userProfile) {
            if (userProfile.aiConsentEnabled) {
                if (!hasGreeted) {
                    setHasGreeted(true);
                    const startConversation = async () => {
                        const greeting = "Hello! I'm the support assistant. How can I help you today? Are you reporting a technical problem, another user, or a specific puzzle?";
                        setMessages([{ role: 'model', text: greeting }]);
                        setIsLoading(false);
                        playAudio(greeting);
                    };
                    startConversation();
                }
            } else {
                setShowConsentModal(true);
            }
        }
    }, [userProfile, userProfile?.aiConsentEnabled, hasGreeted]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // FIX: Moved useEffect from JSX to the component body to correctly handle side effects.
    useEffect(() => {
        if (status === 'complete') {
            const timer = setTimeout(() => {
                pageContext?.goBack();
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [status, pageContext]);
    
    const playAudio = async (text: string) => {
        if (isMuted || !text) return;
        initAudioContext();
        if (!audioContextRef.current) return;

        const base64Audio = await generateSpeechFromText(text);
        if (base64Audio && audioContextRef.current) {
            try {
                const audioData = decode(base64Audio);
                const audioBuffer = await decodeAudioData(audioData, audioContextRef.current, 24000, 1);
                const source = audioContextRef.current.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(audioContextRef.current.destination);
                source.start();
            } catch (error) {
                console.error("Failed to play audio:", error);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading || status !== 'chatting') return;

        initAudioContext(); // Ensure audio is ready on user interaction
        const userMessage: Message = { role: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        const aiResponseText = await getAIChatbotResponse([...messages, userMessage], input);
        
        if (aiResponseText) {
            const aiMessage: Message = { role: 'model', text: aiResponseText };
            setMessages(prev => [...prev, aiMessage]);
            playAudio(aiResponseText);

            // Check for conversation end
            if (aiResponseText.includes("I have everything I need")) {
                setStatus('processing');
                const finalTranscript = [...messages, userMessage, aiMessage];
                const triageResult = await processSupportChatForTriage(finalTranscript);
                
                if (triageResult && userProfile) {
                    await submitAIReport({
                        reporterUid: userProfile.uid,
                        reporterDisplayName: userProfile.displayName,
                        reportedAt: new Date() as any, // Firestore will convert this
                        transcript: finalTranscript,
                        ...triageResult
                    });
                }
                setStatus('complete');
            }
        } else {
            // Handle error case
             setMessages(prev => [...prev, { role: 'model', text: "Sorry, I encountered an error. Please try again." }]);
        }

        setIsLoading(false);
    };


    return (
        <div className="h-full flex flex-col bg-gray-900 text-white">
            <header className="flex-shrink-0 flex items-center p-3 pt-[max(0.75rem,env(safe-area-inset-top))] shadow-md bg-gray-800">
                <BackButton colorClass="text-white" text="" />
                <div className="flex-1 text-center">
                    <h1 className="text-lg font-bold">AI Support Assistant</h1>
                </div>
                <button onClick={() => setIsMuted(!isMuted)} className="text-white">
                    <i className={`ph-duotone ${isMuted ? 'ph-speaker-slash' : 'ph-speaker-high'} text-2xl`}></i>
                </button>
            </header>

            <main className="flex-grow p-4 overflow-y-auto space-y-4">
                {messages.map((msg, index) => (
                    <div key={index} className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'model' && <i className="ph-duotone ph-robot text-2xl self-start flex-shrink-0"></i>}
                        <div className={`max-w-xs md:max-w-md p-3 rounded-2xl ${msg.role === 'user' ? 'bg-blue-600 rounded-br-none' : 'bg-gray-700 rounded-bl-none'}`}>
                            <p className="text-sm">{msg.text}</p>
                        </div>
                    </div>
                ))}
                {isLoading && (
                     <div className="flex items-end gap-2 justify-start">
                        <i className="ph-duotone ph-robot text-2xl self-start"></i>
                        <div className="max-w-xs md:max-w-md p-3 rounded-2xl bg-gray-700 rounded-bl-none">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"></div>
                            </div>
                        </div>
                    </div>
                )}
                {status === 'complete' && (
                    <div className="p-4 text-center bg-emerald-800 rounded-lg">
                        <p className="font-semibold">Thank you! Your report has been submitted.</p>
                        <p className="text-sm text-emerald-200">Our team will review it shortly. You will now be returned to the settings page.</p>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </main>

            <footer className="flex-shrink-0 p-3 bg-gray-800 border-t border-gray-700">
                <form onSubmit={handleSubmit} className="flex items-center space-x-3">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Describe your issue..."
                        className="flex-1 bg-gray-700 border-gray-600 rounded-full px-4 py-2 focus:ring-purple-500 focus:border-purple-500"
                        disabled={isLoading || status !== 'chatting'}
                    />
                    <button type="submit" disabled={!input.trim() || isLoading || status !== 'chatting'} className="bg-purple-600 text-white rounded-full h-10 w-10 flex items-center justify-center disabled:bg-gray-500">
                        <i className="ph-duotone ph-paper-plane-tilt text-xl"></i>
                    </button>
                </form>
            </footer>

            <AIConsentModal
                isOpen={showConsentModal}
                onClose={() => pageContext?.goBack()}
                onConsentGranted={() => setShowConsentModal(false)}
            />
        </div>
    );
};

export default AISupportChatPage;
